import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilterData as setFilterDataInStore } from "../../../store/slices/filterDataSlice";
import axiosInstance from "../../../api/Axios";

export default function useCommonListing({
  userInfo,
  passSize = 15,
  passPage = 1,
  passURL = "",
  loadAgain = false,
  includeStatus = true,
  takeFromData = (res) => res?.data?.results,
  takeCountFrom = (res) => res?.data?.count,
  useOldPagination = false,
  persist = false,
  listId = null,
  noPaginationAndFilters = false,
}) {
  const dispatch = useDispatch();

  // Default filter state
  const defaultFilterData = {
    pageSize: passSize,
    currentPage: passPage,
    url: passURL,
    search: "",
    active: true,
    filter_url: "",
    sort_column: null,
    sort_order: null,
  };

  // Get saved filters from Redux (if any)
  const savedFilterData = useSelector((state) =>
    listId ? state.filterData[listId] : null
  );

  const [filterData, setFilterData] = useState(defaultFilterData);
  const [data, setData] = useState([]);
  const [itemsLength, setItemsLength] = useState(0);
  const [loading, setLoading] = useState(true);
  const [classification, setClassification] = useState(null);
  const [isHydrated, setIsHydrated] = useState(!persist); // only wait when persist is true

  const page = useOldPagination ? "page_num" : "page";
  const size = useOldPagination ? "items" : "page_size";

  // 🧠 Hydrate from Redux store (persist mode)
  useEffect(() => {
    if (persist && listId && savedFilterData) {
      setFilterData((prev) => ({ ...prev, ...savedFilterData }));
      console.log("Hydrated from store:", listId, savedFilterData);
    }
    setIsHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 💾 Save to Redux store when filterData changes (persist mode only)
  useEffect(() => {
    if (persist && listId && isHydrated) {
      const dataToStore = {
        ...filterData,
        sort_column: null,
        sort_order: null,
      };
      dispatch(setFilterDataInStore({ listId, data: dataToStore }));
      console.log("Persisted to store:", listId, dataToStore);
    }
  }, [filterData, persist, listId, dispatch, isHydrated]);

  // 📡 Fetch API when filters change
  const isEmptyObject = (obj) =>
    obj &&
    typeof obj === "object" &&
    !Array.isArray(obj) &&
    Object.keys(obj).length === 0;
  useEffect(() => {
    if (!isHydrated) return;

    let passUrl = filterData.url;
    const joiner = passUrl.includes("?") ? "&" : "?";
    const statusParam = includeStatus
      ? `&status=${filterData.active ? "True" : "False"}`
      : "";

    if (noPaginationAndFilters) {
      passUrl = filterData.url;
    } else {
      if (filterData.search?.length > 0) {
        if (filterData.search.length > 1) {
          passUrl += `${joiner}${page}=${filterData.currentPage}&${size}=${filterData.pageSize}&search=${filterData.search}${statusParam}`;
          if (filterData.filter_url) passUrl += `&${filterData.filter_url}`;
        } else {
          return; // stop if search is only 1 char
        }
      } else {
        passUrl += `${joiner}${page}=${filterData.currentPage}&${size}=${filterData.pageSize}${statusParam}`;
        if (filterData.filter_url) passUrl += `&${filterData.filter_url}`;
      }

      if (filterData.sort_column && filterData.sort_order) {
        passUrl += `&sort_column=${filterData.sort_column}&sort_order=${filterData.sort_order}`;
      }
    }
    setLoading(true);
    axiosInstance
      .get(passUrl)
      .then((res) => {
        const extracted = takeFromData(res);
        setData(isEmptyObject(extracted) ? [] : extracted);
        setItemsLength(takeCountFrom(res) || 0);
        // Extract classification from API response
        if (res?.data?.classification) {
          setClassification(res.data.classification);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, isHydrated, loadAgain, userInfo?.data?.organization?.id]);

  // 🧩 Pagination + Filter handlers
  const onShowSizeChange = (current, size) => {
    setFilterData((prev) => ({ ...prev, pageSize: size }));
  };

  const onPageChange = (page, pageSize) => {
    setFilterData((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (event) => {
    setFilterData((prev) => ({
      ...prev,
      search: event.target.value,
      currentPage: 1,
    }));
  };

  const handleSwitchChange = (event) => {
    setFilterData((prev) => ({
      ...prev,
      search: "",
      currentPage: 1,
      active: event,
    }));
  };

  const handleClearAll = () => {
    setFilterData({ ...defaultFilterData });
  };

  const setFilterUrl = (generatedFilterUrl) => {
    setFilterData((prev) => ({
      ...prev,
      search: "",
      currentPage: 1,
      filter_url: generatedFilterUrl,
    }));
  };

  return {
    data,
    itemsLength,
    loading,
    filterData,
    setFilterData,
    onShowSizeChange,
    onPageChange,
    handleSearchChange,
    handleSwitchChange,
    handleClearAll,
    setFilterUrl,
    classification,
    setLoading,
  };
}
